import React, { useState, useEffect, useRef } from "react";
import "./ItemTransferHistoryModal.css";

const ItemTransferHistoryModal = ({ onClose }) => {
  const [query, setQuery]           = useState("");
  const [submitted, setSubmitted]   = useState("");
  const [results, setResults]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef  = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch suggestions with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/item-search-suggestions?q=${encodeURIComponent(query.trim())}`);
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
        setActiveSuggestion(-1);
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSearch = async (searchVal) => {
    const q = (searchVal ?? query).trim();
    if (!q) return;
    setShowSuggestions(false);
    setSuggestions([]);
    setLoading(true);
    setError(null);
    setResults(null);
    setSubmitted(q);
    try {
      const res = await fetch(`/api/item-history?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSuggestion = (s) => {
    const val = s.serial_number || s.imei_number;
    setQuery(val);
    setShowSuggestions(false);
    setSuggestions([]);
    handleSearch(val);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === "Enter") handleSearch();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeSuggestion >= 0) {
        handleSelectSuggestion(suggestions[activeSuggestion]);
      } else {
        handleSearch();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("en-SG", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }).format(d);
  };

  const labelEvent = (type) => {
    const map = {
      created:           "Created",
      transferred:       "Transfer",
      WAREHOUSE_RECEIVE: "Warehouse Receive",
      DEPLOY:            "Deployed",
      COLLECT:           "Collected",
      TRANSFER:          "Transfer",
    };
    return map[type] || type;
  };

  const item    = results?.item;
  const history = results?.history ?? [];

  return (
    <div className="modal-overlay">
      <div className="modal-content ith-modal">
        <button className="close-button" onClick={onClose}>&times;</button>

        <h3 className="ith-title">Item Transfer History</h3>

        {/* ── Search bar ── */}
        <div className="ith-search-row" ref={wrapperRef}>
          <div className="ith-input-wrap">
            <input
              type="text"
              className="ith-search-input"
              placeholder="Enter Serial Number or IMEI…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); }}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              autoComplete="off"
            />
            {query && (
              <button
                className="ith-clear-btn"
                onClick={() => { setQuery(""); setSuggestions([]); setShowSuggestions(false); setResults(null); }}
                title="Clear"
              >×</button>
            )}

            {/* Dropdown */}
            {showSuggestions && (
              <ul className="ith-suggestions">
                {suggestions.map((s, i) => (
                  <li
                    key={s.serial_number || s.imei_number}
                    className={`ith-suggestion-item ${i === activeSuggestion ? "ith-suggestion-active" : ""}`}
                    onMouseDown={() => handleSelectSuggestion(s)}
                  >
                    <div className="ith-sug-main">
                      <span className="ith-sug-serial">{s.serial_number || s.imei_number}</span>
                      {s.imei_number && s.serial_number && (
                        <span className="ith-sug-imei">IMEI: {s.imei_number}</span>
                      )}
                    </div>
                    <div className="ith-sug-meta">
                      <span>{s.asset1_name}</span>
                      {s.asset2_name && <span> · {s.asset2_name}</span>}
                      {s.location_name && <span className="ith-sug-loc"> · {s.location_name}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button className="ith-search-btn" onClick={() => handleSearch()}>Search</button>
        </div>

        {loading && <p className="ith-status">Searching…</p>}
        {error   && <p className="ith-status ith-error">{error}</p>}

        {/* ── Item summary card ── */}
        {item && (
          <div className="ith-item-card">
            <div className="ith-item-row"><span className="ith-label">Model</span>      <span>{item.asset1_name || "—"}</span></div>
            <div className="ith-item-row"><span className="ith-label">Colour</span>     <span>{item.asset2_name || "—"}</span></div>
            <div className="ith-item-row"><span className="ith-label">Serial No.</span> <span>{item.serial_number || "—"}</span></div>
            <div className="ith-item-row">
              <span className="ith-label">IMEI</span>
              <span>{item.imei_number && item.imei_number !== "null" ? item.imei_number : "—"}</span>
            </div>
            <div className="ith-item-row"><span className="ith-label">Type</span>       <span>{item.inventory_type || "—"}</span></div>
            <div className="ith-item-row">
              <span className="ith-label">Current Location</span>
              <span className="ith-current-location">{item.location_name || "—"}</span>
            </div>
          </div>
        )}

        {results && !item && (
          <p className="ith-status ith-empty">No item found for <strong>{submitted}</strong>.</p>
        )}

        {/* ── History table ── */}
        {item && (
          <>
            <h4 className="ith-section-heading">
              Movement History
              {history.length > 0 && (
                <span className="ith-count">{history.length} record{history.length !== 1 ? "s" : ""}</span>
              )}
            </h4>

            {history.length === 0 ? (
              <p className="ith-status ith-empty">No movement history found for this item.</p>
            ) : (
              <div className="ith-table-wrap">
                <table className="ith-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date / Time</th>
                      <th>Event</th>
                      <th>From</th>
                      <th>To / Deployment</th>
                      <th>DO / DM No.</th>
                      <th>Performed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((row, i) => (
                      <tr key={`${row.event_type}-${row.log_id}-${i}`} className={i % 2 === 0 ? "ith-row-even" : ""}>
                        <td className="ith-seq">{i + 1}</td>
                        <td className="ith-nowrap">{formatDate(row.event_at)}</td>
                        <td>
                          <span className={`ith-event-badge ith-event-${(row.event_type || "").toLowerCase()}`}>
                            {labelEvent(row.event_type)}
                          </span>
                        </td>
                        <td>{row.from_location_name || "—"}</td>
                        <td>
                          {row.location_or_deployment || "—"}
                          {row.deployment_id && (
                            <span className="ith-dep-id"> #{row.deployment_id}</span>
                          )}
                        </td>
                        <td className="ith-nowrap">{row.delivery_order_number || "—"}</td>
                        <td>{row.performed_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ItemTransferHistoryModal;